/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import { memo, type ReactNode, useCallback } from 'react';
import startCase from 'lodash/startCase';
import Stack from '@mui/material/Stack/Stack';

import { ActionChip } from '@marketplace/gather-ui/components/chip';
import { Typography } from '@marketplace/gather-ui/components/typography';

import useSearchOptions from '@/rest/hooks/use-search-options';
import { useSearchDropdown } from '../search-dropdown.provider';
import { type UpdateSearchValueParams } from '../../search.component';

const styles = { borderColor: 'divider' };

type PopularMakesProps = {
  make: string;
  selectMake(make: string): Promise<void>;
};

const PopularMake = memo(function PopularMake({ make, selectMake }: PopularMakesProps): ReactNode {
  const handleClick = useCallback(() => selectMake(make), [selectMake, make]);

  return <ActionChip key={make} label={startCase(make)} sx={styles} onClick={handleClick} />;
});

type QuickPopularMakesProps = {
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
};

export function QuickPopularMakes({ updateSearchValue }: QuickPopularMakesProps): ReactNode {
  const { closeDropdown } = useSearchDropdown();

  // Fetch search options from dedicated API (vehicle styles + popular makes)
  const searchOptionsState = useSearchOptions();

  // Extract vehicle styles and popular makes from API response
  const popularMakes = searchOptionsState.data?.popularMakes ?? [];

  const selectMake = useCallback(
    async (make: string): Promise<void> => {
      await updateSearchValue({
        searchValue: make,
        searchType: 'QUICK_MAKE',
        selectedFilter: { make, modelTrims: null },
        autoSubmit: true,
      });
      closeDropdown();
    },
    [closeDropdown, updateSearchValue]
  );

  if (popularMakes.length === 0) {
    return null;
  }

  return (
    <Stack pb={6} pt={4} spacing={4}>
      <Typography color='text.primary' size='small' variant='functional'>
        Popular Makes
      </Typography>

      <Stack direction='row' flexWrap='wrap' gap={3}>
        {popularMakes.map((make: string) => (
          <PopularMake key={make} make={make} selectMake={selectMake} />
        ))}
      </Stack>
    </Stack>
  );
}
